import { useState, useEffect } from 'react'
import {
  FaLinkedin,
  FaGithub,
  FaGlobe,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaDribbble,
  FaBehance,
  FaMedium,
  FaStackOverflow,
  FaEnvelope,
  FaFileDownload,
} from 'react-icons/fa'
import WheelLoader from '../components/common/WheelLoader'
import { DEFAULT_THEME } from '../constants/theme'
import { useGoogleFont } from '../hooks/useGoogleFont'
import { profileService, type Profile } from '../services/profileService'
import { getUrlConfig, getDisplayDomain } from '../utils/url'

interface ProfilePageProps {
  username: string
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getLocation(profile: Profile): string {
  return [profile.city, profile.state, profile.country].filter(Boolean).join(', ')
}

export default function ProfilePage({ username }: ProfilePageProps) {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)

  const siteName = getDisplayDomain()

  // Get theme values from profile
  const themeColors = {
    primary: profile?.theme_primary_color || DEFAULT_THEME.primary,
    secondary: profile?.theme_secondary_color || DEFAULT_THEME.secondary,
    font: profile?.theme_font || DEFAULT_THEME.font,
  }

  // Load Google Font
  useGoogleFont(themeColors.font)

  useEffect(() => {
    async function fetchProfile() {
      if (!username) return
      try {
        const result = await profileService.getProfileByUsername(username)
        if (result.profile) {
          setProfile(result.profile)
          document.title = `${result.profile.full_name} | ${siteName}`
        } else if (result.error === 'This profile is private') {
          setIsPrivate(true)
        } else {
          setError('Profile not found')
        }
      } catch {
        setError('Profile not found')
      } finally {
        setLoading(false)
      }
    }
    fetchProfile()
  }, [username, siteName])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4">
            <WheelLoader width={100} />
          </div>
          <p className="text-gray-500">Loading profile...</p>
        </div>
      </div>
    )
  }

  // Show private profile message
  if (isPrivate) {
    const { mainAppUrl } = getUrlConfig()
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">This profile is private</h1>
          <p className="text-gray-500 mb-6">
            The owner of this profile has chosen to keep it private.
          </p>
          <a
            href={mainAppUrl}
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Go to {siteName}
          </a>
        </div>
      </div>
    )
  }

  // Redirect to homepage if profile not found
  if (error || !profile) {
    const { mainAppUrl } = getUrlConfig()
    window.location.href = mainAppUrl
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Redirecting...</p>
        </div>
      </div>
    )
  }

  const location = getLocation(profile)
  const { mainAppUrl } = getUrlConfig()

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50/30"
      style={{ fontFamily: `'${themeColors.font}', sans-serif` }}
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
          }}
        />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />

        <div className="relative max-w-4xl mx-auto px-4 py-16 sm:py-20">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Avatar */}
            <div className="w-28 h-28 sm:w-32 sm:h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-3xl sm:text-4xl font-bold border-4 border-white/30">
              {getInitials(profile.full_name)}
            </div>

            {/* Info */}
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                {profile.full_name}
              </h1>
              {profile.headline && (
                <p className="text-xl text-indigo-100 mb-3">{profile.headline}</p>
              )}
              {location && (
                <p className="text-indigo-200 flex items-center justify-center sm:justify-start gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {location}
                </p>
              )}

              {/* Social Links */}
              <div className="flex items-center justify-center sm:justify-start gap-3 mt-4 flex-wrap">
                {profile.email && (
                  <a
                    href={`mailto:${profile.email}`}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Email"
                  >
                    <FaEnvelope className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="LinkedIn"
                  >
                    <FaLinkedin className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.github_url && (
                  <a
                    href={profile.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="GitHub"
                  >
                    <FaGithub className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.twitter_url && (
                  <a
                    href={profile.twitter_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Twitter"
                  >
                    <FaTwitter className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.instagram_url && (
                  <a
                    href={profile.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Instagram"
                  >
                    <FaInstagram className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.youtube_url && (
                  <a
                    href={profile.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="YouTube"
                  >
                    <FaYoutube className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.dribbble_url && (
                  <a
                    href={profile.dribbble_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Dribbble"
                  >
                    <FaDribbble className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.behance_url && (
                  <a
                    href={profile.behance_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Behance"
                  >
                    <FaBehance className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.medium_url && (
                  <a
                    href={profile.medium_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Medium"
                  >
                    <FaMedium className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.stackoverflow_url && (
                  <a
                    href={profile.stackoverflow_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Stack Overflow"
                  >
                    <FaStackOverflow className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.website_url && (
                  <a
                    href={profile.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Website"
                  >
                    <FaGlobe className="w-5 h-5 text-white" />
                  </a>
                )}
                {profile.allow_resume_download && profile.resume_url && (
                  <a
                    href={profile.resume_url}
                    download
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Download Resume"
                  >
                    <FaFileDownload className="w-5 h-5 text-white" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Summary */}
        {profile.summary && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                style={{ color: themeColors.primary }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              About
            </h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-line">{profile.summary}</p>
          </section>
        )}

        {/* Experience */}
        {profile.work_experiences.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                style={{ color: themeColors.primary }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              Experience
            </h2>
            <div className="space-y-8">
              {profile.work_experiences.map((exp) => (
                <div
                  key={exp.id}
                  className="relative pl-6 border-l-2"
                  style={{ borderColor: `${themeColors.primary}40` }}
                >
                  <div
                    className="absolute -left-2 top-0 w-4 h-4 rounded-full border-2 border-white"
                    style={{ backgroundColor: themeColors.primary }}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{exp.job_title}</h3>
                      <p style={{ color: themeColors.primary }}>{exp.company_name}</p>
                    </div>
                    <div className="text-sm text-gray-500">
                      <span>
                        {formatDate(exp.start_date)} -{' '}
                        {exp.is_current ? 'Present' : formatDate(exp.end_date)}
                      </span>
                      {exp.location && (
                        <span className="block sm:inline sm:ml-2">• {exp.location}</span>
                      )}
                    </div>
                  </div>
                  {exp.description && (
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                      {exp.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Education */}
        {profile.educations.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                style={{ color: themeColors.secondary }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222"
                />
              </svg>
              Education
            </h2>
            <div className="space-y-6">
              {profile.educations.map((edu) => (
                <div
                  key={edu.id}
                  className="relative pl-6 border-l-2"
                  style={{ borderColor: `${themeColors.secondary}40` }}
                >
                  <div
                    className="absolute -left-2 top-0 w-4 h-4 rounded-full border-2 border-white"
                    style={{ backgroundColor: themeColors.secondary }}
                  />
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 mb-2">
                    <div>
                      <h3 className="font-semibold text-gray-900">{edu.institution_name}</h3>
                      <p style={{ color: themeColors.secondary }}>
                        {edu.degree}
                        {edu.field_of_study && ` in ${edu.field_of_study}`}
                      </p>
                    </div>
                    <div className="text-sm text-gray-500">
                      {(edu.start_date || edu.end_date) && (
                        <span>
                          {new Date(edu.start_date || '').getFullYear()} -{' '}
                          {new Date(edu.end_date || '').getFullYear()}
                        </span>
                      )}
                      {edu.gpa && <span className="ml-2">• GPA: {edu.gpa}</span>}
                    </div>
                  </div>
                  {edu.description && <p className="text-gray-600 text-sm">{edu.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Projects */}
        {profile.projects.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
              <svg
                className="w-5 h-5"
                style={{ color: themeColors.primary }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              Projects
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              {profile.projects.map((project) => (
                <div
                  key={project.id}
                  className="group p-5 border border-gray-100 rounded-xl hover:border-indigo-200 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                      {project.name}
                    </h3>
                    {project.url && (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </a>
                    )}
                  </div>
                  {project.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{project.description}</p>
                  )}
                  {project.technologies && (
                    <div className="flex flex-wrap gap-1.5">
                      {project.technologies.split(',').map((tech, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium"
                        >
                          {tech.trim()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="text-center py-8">
          <p className="text-sm text-gray-400">
            Profile created with{' '}
            <a
              href={mainAppUrl}
              className="font-medium hover:opacity-80"
              style={{ color: themeColors.primary }}
            >
              {siteName}
            </a>
          </p>
        </footer>
      </div>
    </div>
  )
}
